import { getItem } from '@wyn/content';
import type { HagglingView } from '@wyn/contracts';
import { useState } from 'react';
import { coin } from '../lib/time.ts';
import type { TurnGameProps } from './registry.tsx';

/** The Market Cross: name your price for the ware on the table. The trader's limit stays on the server. */
export function HagglingGame({ view, busy, onMove }: TurnGameProps<HagglingView>) {
  const [price, setPrice] = useState(() => Math.round(view.ware.value * 1.5));
  const done = view.over !== null;
  const name = getItem(view.ware.itemKey).name.toLowerCase();
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-dim">
        It is worth {coin(view.ware.value)} at market, and {view.trader.toLowerCase()} carries at most {coin(view.purse)}. Name your price: anything at or under what they will secretly pay is accepted on the spot. Watch their reactions, though tells can lie. Push too hard and they walk away.
      </p>
      <div className="flex flex-wrap justify-between gap-2">
        <span><span className="capitalize">{view.ware.quality}</span> {name}</span>
        <span className="font-mono text-sm" aria-label={`Patience ${view.patience} of ${view.maxPatience}`}>
          Patience {'●'.repeat(Math.max(0, view.patience))}{'○'.repeat(Math.max(0, view.maxPatience - view.patience))}
        </span>
      </div>
      <p className="border-l-2 border-gilt/60 pl-3" role="status">{view.say}</p>
      {!view.walkedOff ? (
        <>
          <label className="block space-y-1">
            <span className="flex justify-between text-sm"><span className="text-ink-dim">Your ask</span><b className="font-mono text-gilt">{coin(price)}</b></span>
            <input
              type="range"
              className="w-full accent-gilt"
              min={view.minPrice}
              max={view.maxPrice}
              step={1}
              value={price}
              disabled={busy || done}
              onChange={(e) => setPrice(Number(e.target.value))}
              aria-label="Your ask"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn py-3" disabled={busy || done} onClick={() => onMove({ type: 'ask', price })}>Name price</button>
            <button type="button" className="btn py-3" disabled={busy || done || view.counter === null} onClick={() => onMove({ type: 'take' })}>
              {view.counter === null ? 'Take offer' : `Take ${coin(view.counter)}`}
            </button>
          </div>
          <div className="text-right">
            <button type="button" className="btn-quiet" disabled={busy || done} onClick={() => onMove({ type: 'walk' })}>Walk away</button>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn" disabled={busy || done} onClick={() => onMove({ type: 'pawn' })}>Sell to the pawnbroker for {coin(view.pawnPrice)}</button>
          <button type="button" className="btn" disabled={busy || done} onClick={() => onMove({ type: 'keep' })}>Keep it</button>
        </div>
      )}
      {view.limit !== null && <p className="text-xs text-ink-faint">They would have paid up to {coin(view.limit)}.</p>}
    </div>
  );
}
